import { useState, useCallback, memo, useMemo, useEffect } from "react";
import { PlayIconButton } from "./play-icon-button";
import { SampleLegacyAsset } from "./sample-legacy-asset";
import * as Styled from "./style";
import { useRouter } from "next/router";
import type { VideoLegacySeasonType } from "~/components/blocks/product/type-guard";
import { isTouchDisplay } from "~/const/device";
import { PATHS } from "~/const/paths";
import { useHandleClickByDevice } from "~/hooks/use-handle-click-by-device";
import { useMatchMedia } from "~/hooks/use-match-media";
import { useOutsidePointerdown } from "~/hooks/use-outside-pointerdown";
import { usePurchaseModalActions } from "~/hooks/use-purchase-modal-actions";
import { useQueryString } from "~/hooks/use-query-string";
import { goToWelcomePage } from "~/util/go-to-welcome-page";

let timerId: number;

type VideoLegacySeasonHeaderProps = {
  videoLegacySeasonData: VideoLegacySeasonType;
};

/**
 * グラビア / VR 作品の場合に「作品末端のヘッダーブロック」に表示するコンポーネント
 * - サンプル動画がある場合 : サンプル動画の取得方法で動画を取得して表示すること
 * - サンプル動画がない場合 : packageLargeImageを表示すること
 * - サンプル動画なし & packageLargeImage なしの場合 : packageImageを表示すること
 */
export const VideoLegacySeasonHeader = memo((props: VideoLegacySeasonHeaderProps) => {
  const { videoLegacySeasonData } = props;

  const { push } = useRouter();
  const { season } = useQueryString();
  const { isSpSize } = useMatchMedia();
  const { handleClickByDevice } = useHandleClickByDevice();
  const { pushWithQueriesPurchaseModal } = usePurchaseModalActions();

  const [isPlayBtnDisplay, setIsPlayBtnDisplay] = useState(true);
  const showPlayBtn = useCallback(() => setIsPlayBtnDisplay(true), []);
  const hidePlayBtn = useCallback(() => setIsPlayBtnDisplay(false), []);

  const { ref: articleRef } = useOutsidePointerdown<HTMLElement>({
    onOutsidePointerDown: () => {
      if (isSpSize) return;
      hidePlayBtn();
    },
  });

  const {
    sampleMovie,
    packageLargeImage,
    packageImage,
    content,
    continueWatching,
    rating: { category },
  } = videoLegacySeasonData;

  /** 視聴権利があるかどうかを判定する */
  const isStreamable = useMemo(() => {
    return content.viewingRights.isStreamable;
  }, [season]);

  /** 購入済み商品があるかどうかを判定する */
  const ppvExpiration = useMemo(() => {
    return content?.ppvExpiration;
  }, [season]);

  /** PPV商品があるかどうかを判定する */
  const ppvProducts = useMemo(() => {
    return content.ppvProducts;
  }, [season]);

  /** SVOD商品があるかどうかを判定する */
  const svodProduct = useMemo(() => {
    return content.svodProduct;
  }, [season]);

  /** 再生ボタン押下時に発火するイベントハンドラ */
  const openPurchaseModalWhenPlayBtnClicked = (contentId: string) => {
    pushWithQueriesPurchaseModal(contentId);
  };

  const onPlayBtnPressed = useCallback(async () => {
    if (!isStreamable) {
      if (ppvProducts.length > 0) {
        openPurchaseModalWhenPlayBtnClicked(content.id);
        return;
      }

      if (svodProduct !== undefined && svodProduct !== null) {
        goToWelcomePage();
        return;
      }

      return;
    }

    // レジューム情報がある場合
    if (continueWatching !== undefined && continueWatching !== null) {
      void push({
        pathname: PATHS.VOD_PLAYER,
        query: {
          season,
          content: continueWatching.contentId,
          part: `${continueWatching.partNumber}`,
        },
      });
    } else {
      // レジューム情報がない場合
      void push({
        pathname: PATHS.VOD_PLAYER,
        query: {
          season,
          content: content.id,
          part: `${content.playInfo.parts[0].number}`,
        },
      });
    }
  }, [season]);

  useEffect(() => {
    // MEMO（kimura）: 予告動画を再生しない端末の場合、3秒後に再生/購入ボタンを自動で消すことでユーザーの混乱を招いたので以下の処理を記載する
    if (isSpSize) return;

    timerId = window.setTimeout(() => hidePlayBtn(), 3000);
    return () => clearTimeout(timerId);
  }, [isSpSize]);

  return (
    <Styled.ArtProductHeaderWrap
      ref={articleRef}
      onMouseOut={() => {
        if (isSpSize) return;
        hidePlayBtn();
      }}
      onMouseMove={() => {
        if (isPlayBtnDisplay || isSpSize) return;
        showPlayBtn();
      }}
      {...handleClickByDevice(() => {
        if (!isTouchDisplay || isSpSize) return;
        showPlayBtn();
      })}
    >
      <Styled.DivOverlay />
      <SampleLegacyAsset
        sampleMovie={sampleMovie}
        packageLargeImage={packageLargeImage}
        packageImage={packageImage}
        videoRatingCategory={category}
      />
      <PlayIconButton
        content={content}
        isStreamable={isStreamable}
        ppvExpiration={ppvExpiration}
        ppvProducts={ppvProducts}
        svodProduct={svodProduct}
        continueWatching={continueWatching}
        isPlayBtnDisplay={isPlayBtnDisplay}
        onPlayBtnPressed={onPlayBtnPressed}
      />
    </Styled.ArtProductHeaderWrap>
  );
});

VideoLegacySeasonHeader.displayName = "VideoLegacySeasonHeader";
